import { reduxKey } from '../reducers';
import { BaseStream, StreamItem } from './types';
import { defaultStreamState } from '../constants';

export const getStreamIds = state =>
  (state[reduxKey] && Object.keys(state[reduxKey])) || [];

export const getIsValidStream = (state, streamId: string) =>
  !!state?.[reduxKey]?.[streamId];

export const getStream = <TItems = any, TParams extends object = any>(
  state,
  streamId: string
): BaseStream<TItems, TParams> =>
  (state[reduxKey] && state[reduxKey][streamId]) || defaultStreamState;

export const getStreamType = (state, streamId: string) =>
  getStream(state, streamId).streamType;

export const getStreamIdsByGroupId = (state, groupId: string) => {
  return Object.keys(state[reduxKey]).filter(
    streamId => getGroupId(state, streamId) === groupId
  );
};

export const findItemInStreams = (
  state,
  groupId: string,
  callback: (item: StreamItem) => boolean
) => {
  const streamIds = getStreamIdsByGroupId(state, groupId);
  let foundItem;
  streamIds.forEach(streamId => {
    if (!foundItem) {
      const items = getItems(state, streamId);
      foundItem = items.find(callback);
    }
  });
  return foundItem;
};

export const getGroupId = (state, streamId: string) =>
  getStream(state, streamId).groupId;

export const getStreamParams = <TParams extends object = any>(
  state,
  streamId: string
) => getStream<any, TParams>(state, streamId).streamParams;

export const getStreamParamsSafe = <TParams extends object = any>(
  state,
  streamId: string
): TParams | {} => getStream<any, TParams>(state, streamId).streamParams || {};

export const getStreamParam = (state, streamId: string, paramType: string) =>
  getStreamParamsSafe(state, streamId)[paramType];

export const getItemsPerFetch = (state, streamId: string) =>
  getStream(state, streamId).itemsPerFetch;

export const getItems = <TItems = any>(state, streamId: string) =>
  getStream<TItems>(state, streamId).items || [];

export const getTotal = (state, streamId: string) =>
  getStream(state, streamId).total || 0;

export const isInitialized = (state, streamId: string) =>
  state[reduxKey] !== undefined && state[reduxKey][streamId] !== undefined;

export const isFetching = (state, streamId: string) =>
  getStream(state, streamId).isFetching || false;

export const hasMore = (state, streamId: string) =>
  getStream(state, streamId).hasMore || false;

export const getHasLess = (state, streamId: string) =>
  getStream(state, streamId).hasLess || false;

export const getHasMoreInDirection = (
  state,
  streamId: string,
  direction: 'next' | 'prev' | 'all' = 'next'
) => {
  switch (direction) {
    case 'prev':
      return getHasLess(state, streamId);
    case 'all':
      return hasMore(state, streamId) || getHasLess(state, streamId);
    case 'next':
    default:
      return hasMore(state, streamId);
  }
};

export const getErrorMsg = (state, streamId: string) =>
  getStream(state, streamId).errorMsg;

export const getTotalItemsCount = (state): number => {
  let count = 0;
  const streamIds = getStreamIds(state);
  if (streamIds.length) {
    for (const streamId of streamIds) {
      count += getItems(state, streamId).length;
    }
  }

  return count;
};

export const getFetchFailures = (state, streamId: string) =>
  getStream(state, streamId).fetchFailuresConsecutive || 0;

export const canStreamAutoFetch = (state, streamId: string) => {
  return (
    !isFetching(state, streamId) &&
    !getErrorMsg(state, streamId) &&
    hasMore(state, streamId) &&
    getItems(state, streamId).length === 0
  );
};
